export {};

let deliveryMode: "live" | "secure" = "live";
let settings = {
  shuffleQuestions: true,
  shuffleAnswers: true,
  lockOnVisibilityHidden: true,
  lockOnBlur: false,
  teacherUnlockRequired: true,
  timerEnabled: false,
  durationMinutes: 10,
};
let creatingSecureSession = false;

function getLaunchCard() {
  return document.querySelector<HTMLElement>(".qcm-launch-card");
}

function buildToggle(label: string, key: keyof typeof settings, disabled = false) {
  const wrapper = document.createElement("label");
  wrapper.className = "secure-launch-check";
  const input = document.createElement("input");
  input.type = "checkbox";
  input.checked = Boolean(settings[key]);
  input.disabled = disabled;
  input.addEventListener("change", () => { (settings as any)[key] = input.checked; syncLaunchUi(); });
  const span = document.createElement("span");
  span.textContent = label;
  wrapper.append(input, span);
  return wrapper;
}

function installModePanel() {
  const card = getLaunchCard();
  if (!card || card.dataset.akyo === "true") return;
  const existing = card.querySelector<HTMLElement>("[data-secure-launch-mode]");
  if (existing) {
    if (existing.hidden !== (card.dataset.akyo === 'true')) syncLaunchUi();
    return;
  }
  const modeCards = card.querySelector(".qcm-mode-cards");
  if (!modeCards) return;

  const panel = document.createElement("section");
  panel.className = "secure-launch-panel";
  panel.dataset.secureLaunchMode = "true";
  panel.innerHTML = `<div class="secure-launch-title"><strong>Mode de lancement</strong><span>Choisis ce que voient les élèves sur leur appareil.</span></div><div class="secure-launch-mode-buttons"><button type="button" data-delivery="live"><b>QCM Live — type Kahoot</b><span>Question au tableau, téléphone = A / B / C / D.</span></button><button type="button" data-delivery="secure"><b>QCM individuel sécurisé</b><span>Question et réponses sur l’appareil, ordre différent pour chaque élève.</span></button></div><div class="secure-launch-settings" data-secure-settings></div>`;
  modeCards.insertAdjacentElement("afterend", panel);

  panel.querySelectorAll<HTMLButtonElement>("[data-delivery]").forEach((button) => button.addEventListener("click", () => {
    deliveryMode = button.dataset.delivery === "secure" ? "secure" : "live";
    syncLaunchUi();
  }));
  const settingsHost = panel.querySelector<HTMLElement>("[data-secure-settings]")!;
  settingsHost.append(
    buildToggle("Mélanger les questions", "shuffleQuestions"),
    buildToggle("Mélanger les réponses", "shuffleAnswers"),
    buildToggle("Verrouiller si l’onglet / l’application est masqué", "lockOnVisibilityHidden"),
    buildToggle("Verrouiller aussi sur simple perte de focus", "lockOnBlur"),
    buildToggle("Autorisation professeur obligatoire pour reprendre", "teacherUnlockRequired"),
    buildToggle("Chronomètre", "timerEnabled"),
  );
  const timer = document.createElement("label");
  timer.className = "secure-launch-timer";
  timer.innerHTML = `<span>Durée</span><input type="number" min="1" max="180" value="${settings.durationMinutes}"><small>minutes</small>`;
  timer.querySelector("input")!.addEventListener("input", (event) => { settings.durationMinutes = Math.max(1, Math.min(180, Number((event.target as HTMLInputElement).value) || 10)); });
  settingsHost.append(timer);
  syncLaunchUi();
}

function syncLaunchUi() {
  const panel = document.querySelector<HTMLElement>("[data-secure-launch-mode]");
  if (!panel) return;
  panel.hidden=getLaunchCard()?.dataset.akyo==='true';
  if(panel.hidden)return;
  panel.querySelectorAll<HTMLButtonElement>("[data-delivery]").forEach((button) => button.classList.toggle("active", button.dataset.delivery === deliveryMode));
  const settingsHost = panel.querySelector<HTMLElement>("[data-secure-settings]");
  if (settingsHost) settingsHost.hidden = deliveryMode !== "secure";
  const timer = panel.querySelector<HTMLElement>(".secure-launch-timer");
  if (timer) timer.classList.toggle("disabled", !settings.timerEnabled);
  const launchButton = getLaunchCard()?.querySelector<HTMLButtonElement>(".qcm-launch-button");
  if (launchButton) {
    launchButton.dataset.deliveryMode = deliveryMode;
    if (deliveryMode === "secure") launchButton.innerHTML = "🔒 Préparer le QCM individuel sécurisé";
  }
}

function readLaunchPayload() {
  const card = getLaunchCard();
  if (!card) throw new Error("Écran de lancement introuvable");
  const selects = card.querySelectorAll<HTMLSelectElement>(".qcm-form-grid select");
  const classId = selects[0]?.value ?? "";
  const [theme = "", chapter = ""] = (selects[1]?.value ?? "|||").split("|||");
  const activeMode = card.querySelector<HTMLButtonElement>(".qcm-mode-cards button.active");
  const mode = activeMode?.dataset.qcmMode === "evaluated" ? "evaluated" : "prerequisite";
  const prereqInput = card.querySelector<HTMLInputElement>(".qcm-count-row input");
  const gridInputs = [...card.querySelectorAll<HTMLInputElement>(".qcm-count-grid input")];
  const counts = mode === "prerequisite"
    ? { prerequisites: Number(prereqInput?.value ?? 0) }
    : { prerequisites: Number(gridInputs[0]?.value ?? 0), currentActivities: Number(gridInputs[1]?.value ?? 0), otherActivities: Number(gridInputs[2]?.value ?? 0) };
  return {
    classId,
    mode,
    theme,
    chapter,
    counts,
    security: {
      shuffleQuestions: settings.shuffleQuestions,
      shuffleAnswers: settings.shuffleAnswers,
      lockOnVisibilityHidden: settings.lockOnVisibilityHidden,
      lockOnBlur: settings.lockOnBlur,
      teacherUnlockRequired: settings.teacherUnlockRequired,
      durationMinutes: settings.timerEnabled ? settings.durationMinutes : 0,
    },
  };
}

async function createSecureSession(button: HTMLButtonElement) {
  if (creatingSecureSession) return;
  creatingSecureSession = true;
  const previous = button.innerHTML;
  button.disabled = true;
  button.textContent = "Préparation du QCM sécurisé…";
  try {
    const response = await fetch("/api/secure-qcm/admin/sessions", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(readLaunchPayload()),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error ?? "Lancement impossible");
    window.location.href = `/qcm-secure-admin/${encodeURIComponent(body.session.id)}`;
  } catch (reason) {
    window.alert(reason instanceof Error ? reason.message : "Lancement impossible");
    button.disabled = false;
    button.innerHTML = previous;
    creatingSecureSession = false;
  }
}

document.addEventListener("click", (event) => {
  const target = event.target instanceof Element ? event.target.closest<HTMLButtonElement>(".qcm-launch-button") : null;
  if (!target || deliveryMode !== "secure" || getLaunchCard()?.dataset.akyo==='true') return;
  event.preventDefault();
  event.stopPropagation();
  (event as Event).stopImmediatePropagation();
  void createSecureSession(target);
}, true);

async function redirectSecureLive() {
  if (!window.location.pathname.startsWith("/qcm-admin") || !document.querySelector(".qcm-live-page")) return;
  try {
    const response = await fetch("/api/secure-qcm/admin/active", { credentials: "include", cache: "no-store" });
    if (!response.ok) return;
    const body = await response.json();
    if (body.session?.id) window.location.replace(`/qcm-secure-admin/${encodeURIComponent(body.session.id)}`);
  } catch { /* le QCM admin normal reste utilisable */ }
}

const secureLaunchObserver = new MutationObserver(() => { installModePanel(); void redirectSecureLive(); });
secureLaunchObserver.observe(document.documentElement, { childList: true, subtree: true });
window.setTimeout(() => { installModePanel(); void redirectSecureLive(); }, 0);
